import { motion } from 'framer-motion';

/**
 * Componente de loading elegante com spinner circular animado
 * 
 * Funcionalidades:
 * - Spinner circular animado em loop
 * - Centralizado na tela
 * - Animação suave de rotação
 * - Design minimalista e elegante
 */
interface LoadingSpinnerProps {
  message?: string;
}

/**
 * Variantes de animação para o spinner
 * Rotação contínua em loop infinito
 */
const spinnerVariants = {
  animate: {
    rotate: 360,
    transition: {
      // Rotação contínua sem parar
      duration: 1,
      // Loop infinito
      repeat: Infinity,
      // Easing linear para rotação constante
      ease: 'linear' as const,
    },
  },
};

/**
 * Variantes de animação para o container
 * Fade in suave ao aparecer
 */
const containerVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
};

function LoadingSpinner({ message = 'Carregando...' }: LoadingSpinnerProps) {
  return (
    <motion.div
      className="loading-spinner-container"
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      <div className="loading-spinner-content">
        {/* Spinner circular animado */}
        <motion.div
          className="loading-spinner"
          variants={spinnerVariants}
          animate="animate"
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Círculo externo (fundo) */}
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="currentColor"
              strokeWidth="3"
              strokeOpacity="0.2"
              fill="none"
            />
            {/* Arco animado (indicador de progresso) - rotaciona continuamente */}
            <motion.circle
              cx="24"
              cy="24"
              r="20"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
              strokeDasharray="94.2"
              strokeDashoffset="94.2"
              animate={{
                strokeDashoffset: [94.2, 0, 94.2],
                opacity: [1, 0.9, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                transformOrigin: '24px 24px',
              }}
            />
          </svg>
        </motion.div>
        
        {/* Mensagem de loading */}
        <p className="loading-spinner-message">{message}</p>
      </div>
    </motion.div>
  );
}

export default LoadingSpinner;

